import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { withStepUp } from "@/lib/with-step-up";
import { queryKeys } from "@/lib/query-keys";
import {
  memberApprovalPolicySchema, memberRequestSchema, memberRequestsSchema,
  memberPolicyInputSchema, memberDecisionInputSchema, memberReviewersSchema,
  memberDeskResponseSchema, type MemberDeskSearch,
  type MemberApprovalPolicy, type MemberRequestStatus, type MemberDecision,
} from "./schemas";

function base(tenantId: string, organizationId: string) {
  return `/api/v1/tenants/${encodeURIComponent(tenantId)}/organizations/${encodeURIComponent(organizationId)}`;
}
function scope(tenantId: string, organizationId: string) {
  return ["member-approval", tenantId, organizationId] as const;
}
export function useMemberApprovalPolicy(tenantId: string, organizationId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...scope(tenantId, organizationId), "policy"],
    enabled,
    queryFn: async () => memberApprovalPolicySchema.parse(await apiFetch<unknown>(`${base(tenantId, organizationId)}/member-approval-policy`)),
  });
}
export function useMemberReviewers(tenantId: string, organizationId: string, enabled: boolean) {
  return useQuery({ queryKey: [...scope(tenantId, organizationId), "reviewers"], enabled,
    queryFn: async () => memberReviewersSchema.parse(await apiFetch<unknown>(`${base(tenantId, organizationId)}/member-approval-policy/reviewers`)),
  });
}
export function useMemberRequests(tenantId: string, organizationId: string, status: MemberRequestStatus, cursor: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...scope(tenantId, organizationId), "requests", status, cursor],
    enabled,
    refetchInterval: enabled && status === "pending" ? 30_000 : false,
    queryFn: async () => memberRequestsSchema.parse(await apiFetch<unknown>(`${base(tenantId, organizationId)}/member-requests`, {
      searchParams: { status, cursor },
    })),
  });
}
export function useSetMemberApprovalPolicy(tenantId: string, organizationId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (policy: MemberApprovalPolicy) => {
      const body = memberPolicyInputSchema.parse(policy);
      return withStepUp(async () => memberApprovalPolicySchema.parse(await apiFetch<unknown>(`${base(tenantId, organizationId)}/member-approval-policy`, { method: "PUT", body })));
    },
    onSuccess: async () => {
      await Promise.all([client.invalidateQueries({ queryKey: scope(tenantId, organizationId) }), client.invalidateQueries({ queryKey: ["member-approval-desk", tenantId] }),
        // The Organizations list's Waiting roll-up counts these requests.
        client.invalidateQueries({ queryKey: queryKeys.organizations.all(tenantId) })]);
    },
  });
}
export function useDecideMemberRequest(tenantId: string, organizationId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { requestId: string; decision: MemberDecision; note: string }) => {
      const { requestId, decision, note } = memberDecisionInputSchema.parse(input);
      return withStepUp(async () => memberRequestSchema.parse(await apiFetch<unknown>(`${base(tenantId, organizationId)}/member-requests/${encodeURIComponent(requestId)}/decision`, { method: "POST", body: { decision, note } })));
    },
    onSuccess: async () => {
      await Promise.all([client.invalidateQueries({ queryKey: scope(tenantId, organizationId) }), client.invalidateQueries({ queryKey: ["member-approval-desk", tenantId] }),
        // The Organizations list's Waiting roll-up counts these requests.
        client.invalidateQueries({ queryKey: queryKeys.organizations.all(tenantId) })]);
    },
  });
}

export function memberDeskQueryKey(tenantId: string, search: MemberDeskSearch) {
  return ["member-approval-desk", tenantId, { ...search, status: search.status ?? "pending" }] as const;
}
export async function fetchMemberDesk(tenantId: string, search: MemberDeskSearch) {
  return memberDeskResponseSchema.parse(await apiFetch<unknown>(`/api/v1/tenants/${encodeURIComponent(tenantId)}/member-requests`, {
    searchParams: { ...search, status: search.status ?? "pending" },
  }));
}
export function useMemberDesk(tenantId: string, search: MemberDeskSearch, enabled: boolean) {
  return useQuery({
    queryKey: memberDeskQueryKey(tenantId, search), enabled,
    // Empty pending lists still receive new work. Exact requests stop polling
    // once decided; navigation/focus and mutations resolve their current state.
    refetchInterval: (query) => enabled && (search.request_id
      ? query.state.data?.items.some((request) => request.status === "pending")
      : (search.status ?? "pending") === "pending") ? 30_000 : false,
    queryFn: () => fetchMemberDesk(tenantId, search),
  });
}
